import {
  Box,
  Button,
  createStyles,
  Dialog,
  fade,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Toolbar,
  Tooltip,
  Typography,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import RefreshIcon from '@material-ui/icons/Refresh';
import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import UserDeleteForm from '../../components/UserDeleteForm';
import UserForm from '../../components/UserForm';
import { Person, AppRole, MutationStatusCode } from '../../gql-types.generated';
import { selectNetworkBusy, selectViewerUser } from '../app/AppSlice';
import { deletePersonData, fetchUserList, upsertPersonData } from './UserManagementActions';
import {
  clearError,
  selectUserList,
  selectError,
  selectUpsertPersonStatus,
  selectDeletePersonStatus,
  captureUpsertPersonStatus,
  captureDeletePersonStatus,
} from './UserManagementSlice';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      minWidth: '70%',
    },
    table: {
      minWidth: 650,
    },
    toolbar: {
      borderBottomWidth: 1,
      borderBottomColor: fade(theme.palette.common.black, 0.15),
      borderBottomStyle: 'solid',
    },
    toolbarButton: {
      minWidth: 120,
      marginLeft: 4,
    },
    headerSubtitle: {
      letterSpacing: 1.5,
      fontSize: 12,
      fontWeight: 'normal',
    },
    headerTitle: {
      fontWeight: 'bold',
    },
    title: {
      flex: '1 1 100%',
    },
    skeletonRowText: {
      height: 20,
    },
    tooltip: {
      fontSize: '10pt',
      textAlign: 'center',
      verticalAlign: 'middle',
    },
  }),
);

const UserManagement: FC = () => {
  const classes = useStyles();
  const [user, setUser] = useState<Person | null>();
  const [openModify, setOpenModify] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const dispatch = useDispatch();
  const userError = useSelector(selectError);
  const networkBusy = useSelector(selectNetworkBusy);
  const viewerUser = useSelector(selectViewerUser);
  const upsertPersonStatus = useSelector(selectUpsertPersonStatus);
  const deletePersonStatus = useSelector(selectDeletePersonStatus);

  useEffect(() => {
    // Fetch the user list on component render.
    dispatch(fetchUserList());
  }, []);

  useEffect(() => {
    // Fetch the user list when a successful mutation occurs.
    if (upsertPersonStatus?.code === MutationStatusCode.Success) {
      // Fetch the user list
      dispatch(fetchUserList());
      // Remove upsert status.
      dispatch(captureUpsertPersonStatus());
    }
    if (deletePersonStatus?.code === MutationStatusCode.Success) {
      // Fetch the user list
      dispatch(fetchUserList());
      // Remove delete status.
      dispatch(captureDeletePersonStatus());
    }
  }, [upsertPersonStatus?.code, deletePersonStatus?.code]);

  const canEdit = viewerUser?.relationship?.role === AppRole.Admin || viewerUser?.relationship?.role === AppRole.Editor;
  const canDeleteUser = (user: Person) => {
    return !user.relationship?.primaryAccountHolder && viewerUser?.id !== user.id;
  };
  const rows = useSelector(selectUserList);
  const onEditUserClick = (row: Person) => {
    // Handle user modify.
    if (row) {
      setUser(row);
    }
    // Clear error on close.
    dispatch(clearError());
    setOpenModify(true);
  };
  const onAddUserClick = () => {
    // Handle add.
    setUser({} as Person);
    // Clear error on close.
    dispatch(clearError());
    setOpenModify(true);
  };
  const onDeleteUserClick = (row: Person) => {
    // Clear error on close.
    dispatch(clearError());
    // Handle delete
    if (row && canDeleteUser(row)) {
      setUser(row);
      setOpenDelete(true);
    }
  };

  const submitDeleteForm = (id: string) => {
    // Clear any error prior to submit.
    dispatch(clearError());
    // Dispatch gql and await to close.
    // How to indicate this is a delete? Change status to deleted?
    dispatch(deletePersonData(id));
  };

  const submitUpsertForm = (firstName: string, lastName: string, email: string, role: AppRole, id?: string) => {
    // Clear any error prior to submit.
    dispatch(clearError());
    // Dispatch gql and await to close.
    dispatch(upsertPersonData(firstName, lastName, email, role, id));
  };

  return (
    <Paper className={classes.paper}>
      <Dialog maxWidth={false} open={openModify} disableBackdropClick disableEscapeKeyDown={networkBusy}>
        <UserForm
          close={() => {
            setOpenModify(false);
            // Clear error on close.
            dispatch(clearError());
            dispatch(fetchUserList());
          }}
          user={user}
          submit={submitUpsertForm}
          userError={userError}
          networkBusy={networkBusy}
        />
      </Dialog>
      <Dialog maxWidth={false} open={openDelete} disableBackdropClick disableEscapeKeyDown={networkBusy}>
        <UserDeleteForm
          close={() => {
            setOpenDelete(false);
            // Clear error on close.
            dispatch(clearError());
          }}
          user={user}
          submit={submitDeleteForm}
          userError={userError}
          networkBusy={networkBusy}
        />
      </Dialog>
      <Toolbar className={classes.toolbar} variant="regular">
        <Box className={classes.title} data-cy="manage-users-header">
          <Typography className={classes.headerSubtitle}>MANAGE USERS</Typography>
          <Typography className={classes.headerTitle}>All Users</Typography>
        </Box>
        <Button
          className={classes.toolbarButton}
          startIcon={<RefreshIcon></RefreshIcon>}
          size="medium"
          variant={'outlined'}
          aria-label={'refresh'}
          color={'primary'}
          onClick={() => {
            console.log('[3] retrieving user list - manual refresh');
            dispatch(fetchUserList());
          }}
          data-cy="refresh"
        >
          Refresh
        </Button>
        {canEdit && (
          <Button
            color="primary"
            variant="outlined"
            aria-label="add"
            className={classes.toolbarButton}
            onClick={onAddUserClick}
            data-cy="add-new-user"
          >
            + NEW USER
          </Button>
        )}
      </Toolbar>
      <TableContainer>
        <Table className={classes.table} aria-label="simple table" data-cy="users-table">
          <TableHead>
            <TableRow>
              <TableCell align="left">Name</TableCell>
              <TableCell align="left">Email</TableCell>
              <TableCell align="left">Account Type</TableCell>
              <TableCell align="left"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows &&
              rows.map((row: Person) => (
                <TableRow key={row.id}>
                  <TableCell align="left">
                    {row.firstName} {row.lastName}
                  </TableCell>
                  <TableCell align="left">{row.email}</TableCell>
                  <TableCell align="left">
                    {Object.keys(AppRole).filter(role => AppRole[role as keyof typeof AppRole] === row.relationship?.role)[0]}
                  </TableCell>
                  <TableCell align="right">
                    {canEdit && (
                      <Tooltip
                        title={<span className={classes.tooltip}>Edit</span>}
                        PopperProps={{
                          popperOptions: {
                            modifiers: {
                              offset: {
                                enabled: true,
                                offset: '0px, -85px',
                              },
                            },
                          },
                        }}
                      >
                        <IconButton aria-label="edit" onClick={() => onEditUserClick(row)} data-cy="edit-row">
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canEdit && canDeleteUser(row) && (
                      <Tooltip
                        title={<span className={classes.tooltip}>Delete</span>}
                        PopperProps={{
                          popperOptions: {
                            modifiers: {
                              offset: {
                                enabled: true,
                                offset: '0px, -85px',
                              },
                            },
                          },
                        }}
                      >
                        <IconButton aria-label="delete" onClick={() => onDeleteUserClick(row)} data-cy="delete-row">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default UserManagement;
